/**
 * Корневой компонент Kaznacheistvo — вход, навигация и разделы.
 *
 * Flow: при монтировании пробуем восстановить сессию по сохранённому токену
 *       (/api/me) → если токена нет или он просрочен, показываем форму входа →
 *       после успешного входа хедер показывает ВЫДАННЫЕ роли разделы
 *       (me.sections), а тело — тот из них, на который указывает адресная
 *       строка; адрес невыданного раздела даёт названный отказ.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - ./api: обёртка REST-клиента (токен, единый формат ошибки);
 *   - ./router: путь адресной строки и переход между разделами.
 *
 * Границы: раздел «Сводка» — сводная картина (карточка 3cd7) и состояние
 * источников (33d5). Платёжный контур вынесен отдельным разделом
 * «PSP/Bank» (карточка BL-2026-09-29-beba): его блоков стало восемь,
 * и на общем экране они оттесняли казначейскую картину, ради которой
 * экран и заводился. Блоки контура скрывают себя сами, пока ни одна
 * платёжная система не включена.
 *
 * ПОЧЕМУ СОСТАВ РАЗДЕЛОВ БОЛЬШЕ НЕ ЗДЕСЬ. Перечень SECTIONS из трёх строк
 * показывался всем целиком: роль `dealing` видела казначейские пункты и
 * получала ошибку, ткнув в них, — навигация обещала то, чего нет. Теперь
 * состав приходит с сервера (GET /api/me, поле sections — техспека
 * BL-2026-10-08-a315 §5), а в коде остаётся только реестр
 * «код раздела → компонент» (SECTION_VIEWS). Фильтрация прежнего списка по
 * роли этого бы не решила: решение о доступе так и осталось бы в двух
 * несвязанных местах — предикаты в Go и список в JSX, — и разошлось бы при
 * первом же изменении, причём молча.
 *
 * ПОЧЕМУ появился роутинг. Прежде здесь стояло «роутинга нет намеренно»:
 * панели одного экрана в адресе не нуждаются. Второй РАЗДЕЛ — Dealing Desk
 * (карточка BL-2026-09-16-90e6, решение §8 п. 7) — это допущение снимает:
 * два экрана без адреса нельзя ни прислать ссылкой, ни пережить обновление
 * страницы. Маршрутизация взята минимальная, своя (см. router.js — там же
 * разобрано, почему не react-router-dom).
 */
import { useCallback, useEffect, useState } from "react";
import { getToken, login, me, setToken } from "./api.js";
import { resolveSectionAccess, useRoute } from "./router.js";
import { roleTitle } from "./roles.js";
import DashboardPanel from "./DashboardPanel";
import CryptoValuationPanel from "./CryptoValuationPanel";
import ReconBreaksPanel from "./ReconBreaksPanel";
import ReconSummaryPanel from "./ReconSummaryPanel";
import PspBalancesPanel from "./PspBalancesPanel";
import PspFlowStatsPanel from "./PspFlowStatsPanel";
import PspTotalsPanel from "./PspTotalsPanel";
import BankTotalsPanel from "./BankTotalsPanel";
import PspTransactionsPanel from "./PspTransactionsPanel";
import SourcesPanel from "./SourcesPanel";
import DealingDeskPanel from "./DealingDeskPanel";
import SettingsSection from "./SettingsPanel";
import BankStatementsSection from "./BankStatementsSection";

// Названия ролей переехали в ./roles.js: тот же перечень понадобился
// разделу «Настройки» для выпадающего списка при заведении и правке
// учётной записи (карточка BL-2026-10-08-5204), а две копии таблицы
// названий разошлись бы — ровно так роль `dealing` однажды и показалась
// в хедере собственным кодом.

function LoginForm({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(username, password);
      setToken(data.access_token);
      // Профиль берём из /api/me, а не из ответа входа, хотя запись есть
      // и там. Выдача разделов (me.sections) — часть профиля, и единственная
      // ручка, про которую контракт это утверждает, — /api/me (техспека
      // BL-2026-10-08-a315 §5). Взяв запись из ответа входа, интерфейс
      // показал бы «разделы не выданы» ровно до первого обновления страницы,
      // если ответ входа выдачу не несёт. Цена — один запрос на вход.
      onSuccess(await me());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <form className="card" onSubmit={submit}>
        <h1>Kaznacheistvo</h1>
        <p className="muted">Внутренняя система казначейства</p>
        {error && <div className="error">{error}</div>}
        <label htmlFor="username">Логин</label>
        <input
          id="username"
          value={username}
          autoComplete="username"
          onChange={(e) => setUsername(e.target.value)}
        />
        <label htmlFor="password">Пароль</label>
        <input
          id="password"
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" disabled={busy || !username || !password}>
          {busy ? "Проверяем…" : "Войти"}
        </button>
      </form>
    </div>
  );
}

/**
 * Раздел «Сводка» — казначейская картина и состояние источников.
 *
 * @param {object} user — текущий пользователь; роль решает, какие блоки
 *        вообще запрашивают данные
 *
 * ЧЕГО ЗДЕСЬ БОЛЬШЕ НЕТ. Администратору показывалась строка «Учётных
 * записей: N. Управление — через API /api/users» — она сообщала ровно об
 * отсутствии экрана управления. Экран появился разделом «Настройки»
 * (карточка BL-2026-10-08-5204), и строка вместе с запросом списка записей
 * из «Сводки» убрана: сводная картина казначейства к учётным записям
 * отношения не имеет.
 */
function SummarySection({ user }) {
  return (
    <>
      <DashboardPanel />
      <SourcesPanel user={user} />
    </>
  );
}

/**
 * Раздел «PSP/Bank» — платёжный контур целиком.
 *
 * @param {object} user — текущий пользователь; роль решает, показывать ли
 *        разбор расхождений и привязку клиентов
 *
 * Сверху — сводные показатели, под ними подробности теми же блоками,
 * что стояли прежде в «Сводке»: сначала ответ на вопрос «сколько и что
 * происходит», затем строки, из которых этот ответ сложился.
 *
 * # ПОЧЕМУ ДВЕ КОЛОНКИ, А НЕ СКОЛЬКО ПОМЕСТИТСЯ
 *
 * Прежняя раскладка (.columns) набирала столько колонок, сколько влезало
 * по 320 пикселей: на FullHD выходило четыре, и таблицы в них обрезались —
 * у движений пропадали колонки, хотя места на экране было вдоволь. Плотная
 * таблица платёжного контура требует около 750 пикселей, и колонок должно
 * быть ДВЕ, а не «сколько влезет»: ширина колонки здесь задана содержимым,
 * а не экраном.
 *
 * Сводные показатели — исключение и стоят ЧЕТЫРЬМЯ колонками: данных в них
 * немного, и в две они пустовали. Чтобы это стало возможно, два блока
 * перестроены под узкую колонку — см. PspTotalsPanel и PspFlowStatsPanel.
 */
function PspBankSection({ user }) {
  return (
    <>
      {/* Сводные показатели: остаток, банковская сторона, обороты, сверка.
          Четырьмя колонками — данных в них немного (см. .columns-4). */}
      <div className="columns-4">
        <PspTotalsPanel />
        <BankTotalsPanel />
        <PspFlowStatsPanel />
        <ReconSummaryPanel />
      </div>
      {/* Подробности — теми же двумя колонками.
          Порядок: остатки и движения в первом ряду, разбор и оценка
          во втором. Слева то, что отвечает «сколько есть», справа —
          «что с этим происходило». */}
      <div className="columns-2">
        <PspBalancesPanel />
        <PspTransactionsPanel user={user} />
        <ReconBreaksPanel user={user} />
        <CryptoValuationPanel user={user} />
      </div>
    </>
  );
}

/**
 * Чем рисуется раздел: код раздела → компонент.
 *
 * ПОЧЕМУ РАЗДЕЛЕНИЕ ИМЕННО ТАКОЕ. Здесь больше нет перечня разделов —
 * только сопоставление «код → экран». СОСТАВ разделов (какой роли что
 * выдано, как раздел называется и по какому адресу живёт) — решение
 * администратора: оно живёт в данных (app.section, app.role_section) и
 * приходит в GET /api/me полем sections. А ЧЕМ раздел нарисован — свойство
 * КОДА: компонента в базе нет, он есть только в этом бандле, и выдать
 * разделу экран администратор не может ничем, кроме выкладки.
 *
 * Прежде здесь стоял список SECTIONS с путями и заголовками, и он
 * показывался всем целиком: роль `dealing` видела казначейские пункты и
 * получала ошибку, ткнув в них (техспека BL-2026-10-08-a315, §2). Дублировать
 * сюда заголовок и путь нельзя по той же причине: два источника названия
 * раздела разойдутся молча, а чинить придётся выкладкой.
 */
const SECTION_VIEWS = {
  summary: SummarySection,
  psp_bank: PspBankSection,
  dealing_desk: DealingDeskPanel,
  // «Банковские выписки» (карточка BL-2026-08-15-2024): загрузка выписок,
  // журнал загрузок и справочник банковских счетов. Кому раздел выдан —
  // решает сервер (section_access, по умолчанию treasurer, backoffice,
  // viewer); кто может ЗАГРУЖАТЬ — предикат CanWrite на сервере.
  bank_statements: BankStatementsSection,
  // «Настройки» — такой же раздел каталога, как остальные, а не особая
  // страница вне модели (техспека BL-2026-10-08-5204 §3.1). Иначе система,
  // которая учит разделы выдавать ролям, сама же сделала бы исключение для
  // своего главного экрана. Выдача разделу не требуется: администратор
  // видит все разделы безусловно, а ручки управления закрыты предикатом
  // CanManageUsers — выданный другой роли раздел покажет ей пустой экран с
  // отказами, и это ВЕРНОЕ поведение: право заводить пользователей даёт не
  // раздел.
  settings: SettingsSection,
};

/**
 * Умеет ли этот бандл нарисовать раздел с таким кодом.
 *
 * @param {string} code — код раздела из выдачи
 * @returns {boolean}
 *
 * hasOwnProperty, а не `SECTION_VIEWS[code]`: код раздела приходит с сервера,
 * и значение вида "constructor" или "toString" нашлось бы в прототипе объекта
 * и притворилось бы компонентом.
 */
function canRenderSection(code) {
  return Object.prototype.hasOwnProperty.call(SECTION_VIEWS, code);
}

/**
 * Названный отказ: раздел по этому адресу роли не выдан.
 *
 * Показывается при прямом переходе — по ссылке, закладке или обновлению
 * страницы (техспека §6 п. 2). Не пустой экран и не молчаливый сброс на
 * первый раздел: человек пришёл по ссылке и должен понять, почему не попал.
 * Адресную строку при этом НЕ правим — подмена скрыла бы, куда он шёл.
 */
function SectionDenied() {
  return (
    <div className="card">
      <h2>Раздел недоступен вашей роли</h2>
      <p className="muted">
        Доступные вам разделы перечислены в хедере. Если раздел нужен для
        работы, его открывает администратор.
      </p>
    </div>
  );
}

/**
 * Роли не выдано ни одного раздела.
 *
 * Отдельный экран, а не пустое тело страницы (техспека §6 п. 3): хедер без
 * пунктов и пустая середина выглядят поломкой приложения, и человек будет
 * обновлять страницу вместо того, чтобы написать администратору.
 */
function NoSectionsGranted() {
  return (
    <div className="card">
      <h2>Разделы не выданы, обратитесь к администратору</h2>
      <p className="muted">
        Вход выполнен, но ни один раздел вашей роли не открыт. Доступ выдаёт
        администратор системы.
      </p>
    </div>
  );
}

/**
 * Раздел выдан, но этот бандл его рисовать не умеет.
 *
 * @param {object} section — раздел из выдачи (code, title, path)
 *
 * ПОЧЕМУ ТАК, А НЕ «СПРЯТАТЬ ПУНКТ». Такой раздел означает ровно одно:
 * интерфейс старше сервера — бэкенд выложен с новым разделом раньше, чем
 * фронтенд, который его рисует. Состояние переходное и лечится обновлением
 * страницы, поэтому важно НАЗВАТЬ причину.
 *
 * Пункт меню при этом остаётся на месте: раздел роли ВЫДАН, и спрятать его
 * означало бы, что фронтенд молча отменяет решение администратора — тот
 * самый дефект «решение о доступе в двух несвязанных местах», от которого
 * карточка и избавляется. Спрятанный пункт к тому же недиагностируем:
 * администратор видит выдачу, пользователь не видит пункта, и объяснить
 * расхождение нечем.
 *
 * Чем это НЕ является: отказом в доступе. Отказ — SectionDenied, и путать
 * их нельзя, потому что действия разные: там идти к администратору, здесь
 * обновить страницу либо дождаться выкладки.
 */
function SectionUnsupported({ section }) {
  return (
    <div className="card">
      <h2>Раздел «{section.title}» пока не отображается</h2>
      <p className="muted">
        Доступ к разделу открыт, но этой версии интерфейса он неизвестен: скорее
        всего, страница загружена до обновления. Обновите страницу — если раздел
        не появился, сообщите администратору.
      </p>
    </div>
  );
}

/**
 * Навигация по разделам в хедере.
 *
 * @param {Array<{code: string, title: string, path: string}>} sections —
 *        разделы, выданные роли (GET /api/me)
 * @param {string} active — путь текущего раздела; null, когда показан отказ
 * @param {function} onGo — переход на путь
 *
 * Пункты строятся ТОЛЬКО из выдачи: пункта закрытого раздела нет, а не
 * «есть, но ругается» (техспека §6 п. 1).
 *
 * Пункты — настоящие ссылки <a href>, а не кнопки: ссылку можно скопировать,
 * открыть в новой вкладке средней кнопкой и прислать коллеге — ровно то,
 * ради чего раздел и получил адрес. Обычный щелчок перехватывается и уходит
 * в pushState, чтобы страница не перезагружалась; щелчок с модификатором
 * (Ctrl, Shift, средняя кнопка) отдаётся браузеру нетронутым — иначе
 * «открыть в новой вкладке» перестало бы работать.
 */
function SectionNav({ sections, active, onGo }) {
  const handle = (event, path) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    onGo(path);
  };

  return (
    <nav className="sections">
      {sections.map((s) => (
        <a
          key={s.code}
          href={s.path}
          className={
            s.path === active ? "section-link section-active" : "section-link"
          }
          aria-current={s.path === active ? "page" : undefined}
          onClick={(e) => handle(e, s.path)}
        >
          {s.title}
        </a>
      ))}
    </nav>
  );
}

/**
 * Подпись пользователя в хедере: имя, роль и реквизиты.
 *
 * @param {object} user — текущий пользователь (full_name, username, role,
 *        position, department)
 *
 * Должность и отдел (техспека §4) дописываются ТОЛЬКО когда непусты: у
 * заведённых прежде записей этих полей нет, и подпись у них остаётся ровно
 * такой, какой была, — без висящего разделителя и без пустого промежутка.
 * Реквизиты попадают в ту же строку, а не отдельным элементом: хедер —
 * flex из трёх частей (styles.css), и четвёртый узел сдвинул бы навигацию
 * с середины.
 *
 * Реквизиты не влияют на права: доступ даёт роль, а это — сведения о
 * человеке (кто он и к какому отделу относится).
 */
function UserBadge({ user }) {
  const details = [user.position, user.department]
    .filter((v) => typeof v === "string" && v.trim() !== "")
    .join(" · ");

  return (
    <span className="muted">
      {user.full_name || user.username} — {roleTitle(user.role)}
      {details && ` · ${details}`}
    </span>
  );
}

/**
 * Каркас приложения после входа: хедер с навигацией и тело текущего раздела.
 *
 * @param {object} user — текущий пользователь; поле sections — выданные роли
 *        разделы (GET /api/me)
 * @param {function} onLogout — выход из сессии
 *
 * И меню, и содержимое берутся из ОДНОЙ выдачи: пункт, которого нет в
 * sections, не нарисуется, а адрес, которого нет в sections, даст названный
 * отказ (resolveSectionAccess). Два места решения — список в JSX и права на
 * сервере — разошлись бы молча, и чинилось бы это выкладкой.
 */
function Shell({ user, onLogout }) {
  const [path, navigate, replace] = useRoute();

  // Выдача приходит с сервера. Поля нет или оно не список — считаем, что
  // разделов не выдано, и говорим это словами. Подставить здесь прежний
  // захардкоженный перечень (чтобы интерфейс пережил выкладку фронтенда
  // раньше бэкенда) значило бы вернуть сам дефект: разделы, которых никто
  // не выдавал. Выкладка идёт одним коммитом — окно рассинхронизации нулевое.
  const sections = Array.isArray(user.sections) ? user.sections : [];
  const view = resolveSectionAccess(path, sections, canRenderSection);
  const Section = view.section ? SECTION_VIEWS[view.section.code] : null;

  useEffect(() => {
    // Корень без выданной «Сводки» ведёт на первый выданный раздел: тело
    // уже показывает его, адресу остаётся догнать. Иначе адресная строка
    // говорила бы одно, а экран показывал другое.
    if (view.kind === "redirect") replace(view.path);
  }, [view.kind, view.path, replace]);

  return (
    <>
      <header>
        <div>
          <strong>Kaznacheistvo</strong> <UserBadge user={user} />
        </div>
        <SectionNav sections={sections} active={view.path} onGo={navigate} />
        <button type="button" onClick={onLogout}>
          Выйти
        </button>
      </header>
      <main>
        {view.kind === "empty" && <NoSectionsGranted />}
        {view.kind === "denied" && <SectionDenied />}
        {view.kind === "unsupported" && (
          <SectionUnsupported section={view.section} />
        )}
        {/* Раздел рисует компонент из реестра; user нужен тем разделам,
            где роль решает, какие блоки запрашивают данные. */}
        {Section && <Section user={user} />}
      </main>
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    // Восстановление сессии: токен может быть просрочен, и единственный
    // надёжный способ это узнать — спросить сервер.
    me()
      .then(setUser)
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  if (loading) {
    return (
      <div className="app">
        <div className="center">
          <p className="muted">Загрузка…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {user ? (
        <Shell user={user} onLogout={logout} />
      ) : (
        <LoginForm onSuccess={setUser} />
      )}
    </div>
  );
}
